import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { combineLatest, debounceTime, filter, map, take } from 'rxjs';
import { SessionQuery, softLockStatuses } from '../state';
import { Cmg1003FileDataQuery } from '../../loan-state';
import { SyncType } from '../../models';
import { ElementRef } from '@angular/core';

export const applyPartialEnablingOrDisabling = (enableControls: boolean, controlNames: string[], form: UntypedFormGroup) => {
  controlNames.forEach((controlName) => {
    const control = form.get(controlName);
    if (control) {
      enableControls ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
    }
  });
};

export const applyPartialEnablingOrDisablingForSecondOrThirdLien = (
  enableControls: boolean,
  controlNames: string[],
  form: UntypedFormGroup,
  sessionQuery: SessionQuery,
  fileDataQuery: Cmg1003FileDataQuery,
) => {
  combineLatest({
    all1003Loaded: sessionQuery.select((s) => s.all1003Loaded),
    loanStatus: sessionQuery.select((s) => s.loanStatusId),
    syncType: fileDataQuery.select((f) => f.syncType),
  })
    .pipe(
      filter(({ all1003Loaded, syncType }) => {
        return all1003Loaded && (syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien);
      }),
      debounceTime(100),
      map(({ loanStatus }) => {
        if (softLockStatuses.includes(loanStatus)) {
          applyPartialEnablingOrDisabling(enableControls, controlNames, form);
        }
      }),
      take(1),
    )
    .subscribe();
};

export const scrollToFirstInvalidControlAndSetFocus = (scrollContainer: ElementRef<HTMLElement>, ignoreFormElement = false) => {
  setTimeout(() => {
    const invalidInputs = scrollContainer.nativeElement.querySelectorAll('.ng-invalid');
    if (invalidInputs) {
      let topMostElement: Element;
      invalidInputs.forEach((element) => {
        if (!topMostElement && ignoreFormElement && element.tagName !== 'FORM') {
          topMostElement = element;
        }
        topMostElement =
          element.tagName !== 'FORM' && element.getBoundingClientRect().top < topMostElement.getBoundingClientRect().top ? element : topMostElement;
      });
      if (topMostElement) {
        topMostElement.scrollIntoView();
        scrollContainer.nativeElement.scrollBy({ top: -70 }); // buffer for label
        if (topMostElement.tagName.toLowerCase() === 'input') {
          (topMostElement as HTMLInputElement).focus();
        } else {
          topMostElement.querySelector('input')?.focus();
        }
      }
    }
  }, 200);
};

/**
 * Marks all controls within a form group or form array as pristine and untouched.
 * @param formElement
 */
export const markAllFormElementsUntouched = (formElement: UntypedFormGroup | UntypedFormArray) => {
  const markControl = (ctrl: any) => {
    if (ctrl instanceof UntypedFormGroup || ctrl instanceof UntypedFormArray) {
      markAllFormElementsUntouched(ctrl);
    } else {
      ctrl?.markAsUntouched({ onlySelf: true });
    }
  };

  if (formElement instanceof UntypedFormGroup) {
    Object.keys(formElement.controls).forEach((key) => {
      markControl(formElement.controls[key]);
    });
  } else if (formElement instanceof UntypedFormArray) {
    formElement.controls.forEach((ctrl) => {
      markControl(ctrl);
    });
  }

  // Also mark the container itself
  formElement.markAsUntouched({ onlySelf: true });
};
